import React, { useState, useCallback, useEffect, useContext } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { rem } from 'polished'
import Calendar from 'components/Common/Calendar'
import { addMonths, endOfMonth, dateIsAfter, dateIsBefore, startOfMonth } from 'lib/dateUtils'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import Heading from '../../Luxkit/Typography/Heading'
import Clickable from '../Clickable'
import LineAngleLeftBIcon from 'components/Luxkit/Icons/line/LineAngleLeftBIcon'
import LineAngleRightBIcon from 'components/Luxkit/Icons/line/LineAngleRightBIcon'
import useDidChange from 'hooks/useDidChange'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'

const Root = styled.div`
  display: flex;
  width: 100%;

  > * {
    flex-grow: 1;

    & + * {
      margin-left: ${rem(52)};
    }
  }
`

const CalendarWrapper = styled.div`
  position: relative;

  > * + * {
    margin-top: ${rem(4)};
  }
`

const Header = styled.div`
  text-align: center;
`

const MonthChangeElement = styled(Clickable)`
  position: absolute;
  top: ${rem(0)};
  transition: color 0.2s;

  &:disabled {
    color: ${props => props.theme.palette.neutral.four};
  }
`

const LeftArrow = styled(MonthChangeElement)`
  left: ${rem(-12)};
`

const RightArrow = styled(MonthChangeElement)`
  right: ${rem(-12)};
`

interface Props {
  onDatesChange: (dates: { startDate: moment.Moment; endDate: moment.Moment | null; }) => void;
  endDate?: moment.Moment;
  startDate?: moment.Moment;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  startLabel?: string;
  endLabel?: string;
}

function DualDateRangePicker(props: Props) {
  const {
    onDatesChange,
    endDate,
    startDate,
    minDate,
    maxDate,
    className,
    startLabel,
    endLabel,
  } = props
  const [date, setDate] = useState<Date>(startDate?.toDate() || new Date())

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onDayClick = useCallback((day: moment.Moment) => {
    if (startDate && !endDate && day.isAfter(startDate)) {
      onDatesChange({ startDate, endDate: day })
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckOut, moment(day).format(ISO_DATE_FORMAT))
    } else {
      onDatesChange({ startDate: day, endDate: null })
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, moment(day).format(ISO_DATE_FORMAT))
    }
  }, [onDatesChange, startDate, endDate, onUserAnalyticsAction])

  const onNextMonth = useCallback(() => {
    setDate(addMonths(date, 1))
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.ShiftMonth, CALENDAR_GA_EVENT_LABEL.NextMonth)
  }, [date, onUserAnalyticsAction])

  const onPrevMonth = useCallback(() => {
    setDate(addMonths(date, -1))
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.ShiftMonth, CALENDAR_GA_EVENT_LABEL.PreviousMonth)
  }, [date, onUserAnalyticsAction])

  const startDateChanged = useDidChange(startDate)
  useEffect(() => {
    if (startDate && startDateChanged &&
      // month on the left === month of the start date
      startDate.month() !== date.getMonth() &&
      // month on the right === month of the start date
      startDate.month() % 12 !== (date.getMonth() + 1) % 12
    ) {
      setDate(startDate.toDate())
    }
  }, [date, startDate, startDateChanged])

  return (<Root className={className}>
    <CalendarWrapper>
      <Header>
        <Heading as="span" variant="heading6">{moment(date).format('MMMM')} {moment(date).format('YYYY')}</Heading>
      </Header>
      <Calendar
        month={date.getMonth()}
        year={date.getFullYear()}
        startDate={startDate?.toDate()}
        minDate={minDate}
        maxDate={maxDate}
        endDate={endDate?.toDate()}
        onDayClick={onDayClick}
        startLabel={startLabel}
        endLabel={endLabel}
      />
      <LeftArrow
        disabled={dateIsBefore(startOfMonth(date), minDate)}
        onClick={onPrevMonth}
      >
        <LineAngleLeftBIcon size={20} />
      </LeftArrow>
    </CalendarWrapper>
    <CalendarWrapper>
      <Header>
        <Heading as="span" variant="heading6">{moment(date).add(1, 'month').format('MMMM')} {moment(date).add(1, 'month').format('YYYY')}</Heading>
      </Header>
      <Calendar
        month={date.getMonth() + 1}
        year={date.getFullYear()}
        startDate={startDate?.toDate()}
        minDate={minDate}
        maxDate={maxDate}
        endDate={endDate?.toDate()}
        onDayClick={onDayClick}
        startLabel={startLabel}
        endLabel={endLabel}
      />
      <RightArrow
        onClick={onNextMonth}
        data-testid="next-month-calendar"
        disabled={dateIsAfter(endOfMonth(addMonths(date, 1)))}
      >
        <LineAngleRightBIcon size={20} />
      </RightArrow>
    </CalendarWrapper>
  </Root>
  )
}

export default React.memo(DualDateRangePicker)
